import { SuccessScreen } from './views/forms/status-lookup/components/SuccessScreen'
import { AxiosResponse } from 'axios'

export const ApiFormIdList = [
  'mail-in-ballot',
  'voter-registration',
  'status-lookup',
] as const
export type ApiFormId = (typeof ApiFormIdList)[number]
export type ValidateFieldType =
  | 'phone'
  | 'email'
  | 'pennDotId'
  | 'ssnLastFour'
  | 'date'
  | 'zip'
  | 'idCard'
  | 'signature'
export type Field = {
  name: string
  type: string
  required?: boolean
  step: number
  validate?: ValidateFieldType
  conditionalOn?: string
  radioGroup?: string
  conditionalOnSelectOption?: string
  conditionalOff?: string
  fixedTodaysDate?: boolean
  dateOnlyYear?: boolean
  maxDefaultDate?: Date
  minDate?: Date
  selectOptions?: string[] | ((input: any) => string[])
  selectLabelsFromOptions?: boolean
  initialValue?: string
  disabled?: boolean
  fieldGroup?: string
  htmlFontSizeSmall?: boolean
  zipInternationalField?: string
  boldedCheckboxLabel?: boolean
  overrideSelectWithTextControllerField?: string
  postalCodeInternationalControllerField?: string
  maxLength?: number
  accordionNoHeaderBottomMargin?: boolean
  customErrorMessage?: boolean
  customRequiredErrorMessage?: boolean
}

export type FieldError = { name: string; errorText: string; errorType: string }
export type BlurEvent = React.FocusEvent<HTMLInputElement, Element>
export type FormConfig = {
  captchaStep: number
  requireEitherEmailOrPhone?: boolean
  pollingOnSubmit?: boolean
  singlePage?: boolean
  afterDateAlert?: Date
  fields: Field[]
  stepHeaders?: StepHeader[]
  headerContent?: HeaderContent
  aboveFieldsContent?: React.FC<AboveBelowFieldsContentType>
  belowFieldsContent?: React.FC<AboveBelowFieldsContentType>
  footerContent?: FooterContent
  validateEventResponse?: ValidateEventFxn
  validateEventResponseType?: 'event' | 'submit' | 'both'
  validateStep?: ValidateStepFxn
  successScreen?: React.FC
  stateChangeHandler?: StateChangeHandlerFxn
  reviewSubmission?: boolean
  requiredFieldGroups?: string[]
}

export type AboveBelowFieldsContentType = {
  step: any
  isReviewing?: boolean
  formComplete: boolean
}

export type StepHeader = {
  text: string
  step: number
}

export type ApiResponse = {
  serverResponse?: AxiosResponse
}

export type Submission = {
  apiResponse: ApiResponse
  formState: {
    [key: string]: any
  }
}

export type ValidateEventFxn = (
  step: number,
  apiResponse: ApiResponse,
  setReplacementContent: React.Dispatch<
    React.SetStateAction<
      | React.ReactElement<any, string | React.JSXElementConstructor<any>>
      | undefined
    >
  >,
  setFormState: (
    value: React.SetStateAction<{
      [key: string]: any
    }>,
  ) => void,
  type: 'event' | 'submit',
) => boolean

export type StateChangeHandlerFxn = (
  key: string,
  prevValue: any,
  newValue: any,
  setFormState: (
    value: React.SetStateAction<{
      [key: string]: any
    }>,
  ) => void,
  allState: any,
) => void

export type ValidateStepFxn = (step: number, state: any) => boolean

export type HeaderContent = React.FC
export type FooterContent = React.FC

const LanguageList = ['en', 'es', 'ko', 'zh']
export type LanguageItem = (typeof LanguageList)[number]

export type i18nStringObject = {
  [lang in LanguageItem]: string
}

type OneStopTarget = 'ovr' | 'mib' | 'plan_polls' | 'plan_early' | 'plan_return'

type ContentBlock = {
  type: 'content'
  small?: boolean
  content: i18nStringObject
}

type CtaBlock = {
  type: 'cta'
  url?: string
  oneStopTarget?: OneStopTarget
  content: i18nStringObject
}

export type DynamicContentBlock = ContentBlock | CtaBlock

export type DynamicFormContent = {
  formOverrides: {
    [FormId in ApiFormId]?: {
      scheduledDowntime?: {
        start: string
        end: string
        content: i18nStringObject
      }
      alert?: {
        enabled?: boolean
        content: i18nStringObject
      }
      success?: {
        bottom?: DynamicContentBlock[]
        vRegNoMib?: DynamicContentBlock[]
      }
      error?: {
        bottom?: DynamicContentBlock[]
      }
    }
  }
  oneStop?: {
    hideMib?: boolean
  }
  dates?: {
    electionDay: {
      date: string
      textVariants: {
        time: i18nStringObject
        timeAndFullDate: i18nStringObject
        fullDate: i18nStringObject
        monthAndDate: i18nStringObject
      }
    }
    voterRegistrationDeadline: {
      date: string
      textVariants: {
        fullDate: i18nStringObject
        monthAndDate: i18nStringObject
      }
    }
    mailInBallotDeadline: {
      date: string
      textVariants: {
        monthAndDate: i18nStringObject
      }
    }
  }
}
